import React, { useMemo, useCallback } from 'react';

import { AboutThisEventModal } from './AboutThisEventModal';
import type { StructuredContent } from '@/types';
import { AboutThisEventEmbedded } from './AboutThisEventEmbedded';
import { StructuredModuleRenderer } from './StructuredModuleRenderer';
import { MEDIA_QUERIES } from '@/components/_shared/constants';
import { useMediaQuery } from '@/hooks';

export type AboutThisEventProps = {
  showFullEventOverview: boolean;
  structuredContent?: StructuredContent;
  handleCloseModal: () => void;
};

export const AboutThisEvent: React.FC<AboutThisEventProps> = React.memo(
  ({ showFullEventOverview, structuredContent, handleCloseModal }) => {
    const isNarrowScreen = useMediaQuery(`(max-width: ${MEDIA_QUERIES.MEDIUM_BREAKPOINT}px)`);

    const memoizedStructuredContent = useMemo(() => structuredContent, [structuredContent]);

    const renderModules = useCallback(
      (keyPrefix: string) =>
        memoizedStructuredContent?.modules?.map((module, index) => (
          <StructuredModuleRenderer key={`${keyPrefix}-${index}`} module={module} />
        )) ?? null,
      [memoizedStructuredContent?.modules],
    );

    const modalChildren = useMemo(
      () => (showFullEventOverview && isNarrowScreen ? renderModules('modal') : null),
      [showFullEventOverview, isNarrowScreen, renderModules],
    );

    const embeddedChildren = useMemo(
      () => (showFullEventOverview && !isNarrowScreen ? renderModules('embedded') : null),
      [showFullEventOverview, isNarrowScreen, renderModules],
    );

    const aboutThisEventModal =
      isNarrowScreen && showFullEventOverview ? (
        <AboutThisEventModal visible={showFullEventOverview} onClose={handleCloseModal}>
          {modalChildren}
        </AboutThisEventModal>
      ) : null;

    const aboutThisEventEmbedded =
      !isNarrowScreen && showFullEventOverview ? (
        <AboutThisEventEmbedded visible>{embeddedChildren}</AboutThisEventEmbedded>
      ) : null;

    return (
      <>
        {aboutThisEventModal}
        {aboutThisEventEmbedded}
      </>
    );
  },
);

AboutThisEvent.displayName = 'AboutThisEvent';
